/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Button, HelpTip, Icon, Text, useBreakpoint } from 'sarsaparilla';
import usePassTypesTable from '../hooks/usePassTypesTable';
import { PassType, PassTypesTableRowDataType } from '../../types/PassType';

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */

interface PassTypesTableProps {
    selectedPassTypeId?: string;
    passTypes: PassType[];
    passTypesRaw: PassType[];
    status?: string;
    showDescription?: boolean;
    pricing?: React.ReactNode;
    onSelectPass: (id: string) => void;
    onRemovePass: (id: string) => void;
    sortFn?: (a: PassType, b: PassType) => number;
}

interface MobilePassTypesTableProps {
    passTypes?: PassType[];
    passTypesRaw?: PassType[];
    isLoading?: boolean;
    showDescription?: boolean;
    pricing?: React.ReactNode;
    getRowData: (sitePass: PassType) => PassTypesTableRowDataType;
    showNonResidentFees?: boolean;
    placeholder: (numCols: number) => React.ReactNode;
}

export default function PassTypesTable({
    selectedPassTypeId,
    passTypes,
    passTypesRaw,
    status,
    showDescription = true,
    pricing = null,
    onSelectPass,
    onRemovePass,
    sortFn,
}: PassTypesTableProps) {
    const { isSmAndDown } = useBreakpoint();
    const { filteredPassTypes, filteredPassTypesRaw, isLoading, getRowData } =
        usePassTypesTable(
            selectedPassTypeId,
            passTypes,
            passTypesRaw,
            status,
            onSelectPass,
            onRemovePass,
            sortFn
        );

    const showNonResidentFees = useMemo(() => {
        return passTypes?.some((passType) => passType?.features?.non_resident_fee);
    }, [passTypes]);

    const placeholder = (numCols: number) => (
        <tr>
            <td colSpan={numCols} className="pass-types-table__no-data-cell">
                <div className="pass-types-table__no-data">No rows found.</div>
            </td>
        </tr>
    );

    const renderRow = (sitePass: PassType) => {
        const {
            id,
            name,
            usedFor,
            durationText,
            price,
            selectedPass,
            nonResidentFee,
            handleAddPass,
            handleRemoveAllPasses,
        } = getRowData(sitePass);
        const isAnnual = durationText === '12 months';

        return (
            <tr key={`pass-type-${id}`} className={selectedPass ? 'highlight' : ''}>
                <td>
                    <Text fontWeight="semibold">
                        {!showDescription && usedFor ? (
                            <HelpTip
                                appearance="dashed"
                                className="mb-half"
                                triggerText={name}
                                content={usedFor}
                            />
                        ) : (
                            name
                        )}
                    </Text>
                    {showDescription && usedFor && (
                        <Text className="pass-types-table__content-item" size="sm">
                            {usedFor}
                        </Text>
                    )}
                    {showNonResidentFees && isAnnual && (
                        <Text className="pass-types-table__content-item" size="sm">
                            Available for U.S. Residents Only
                        </Text>
                    )}
                </td>
                <td>
                    <Text>{durationText}</Text>
                </td>
                <td>
                    <Text>{price}</Text>
                </td>
                {showNonResidentFees && (
                    <td className="non-resident-column">
                        <Text>
                            {nonResidentFee
                                ? `$${nonResidentFee} for each non-resident (16 and over)`
                                : 'Not Applicable'}
                        </Text>
                    </td>
                )}
                <td>
                    {!selectedPass ? (
                        <Button
                            className="pass-types-table__content-item pass-types-table__button"
                            appearance="tertiary"
                            isDisabled={isLoading}
                            onClick={handleAddPass}
                            gaTrackingId="388776844585"
                            gaTrackingValue={name}
                            aria-label={`Add Pass - ${name}`}
                        >
                            Add Pass
                        </Button>
                    ) : (
                        <Button
                            className="pass-types-table__content-item pass-types-table__button pass-types-table__button-selected-pass"
                            appearance="tertiary"
                            iconBeforeElement={<Icon iconName="check-circle" size="md" />}
                            isDisabled={isLoading}
                            onClick={handleRemoveAllPasses}
                            gaTrackingId="262175325497"
                        >
                            Pass Selected
                        </Button>
                    )}
                </td>
            </tr>
        );
    };

    const renderReducedRow = ({ id, name }: Pick<PassType, 'id' | 'name'>) => (
        <tr key={`pass-type-${id}`}>
            <td>
                <Text fontWeight="semibold">{name}</Text>
            </td>
            <td>
                <Text size="sm">Must be purchased on site</Text>
            </td>
        </tr>
    );

    if (isSmAndDown) {
        return (
            <MobilePassTypesTable
                passTypes={filteredPassTypes}
                passTypesRaw={filteredPassTypesRaw}
                isLoading={isLoading}
                showDescription={showDescription}
                pricing={pricing}
                getRowData={getRowData}
                showNonResidentFees={showNonResidentFees}
                placeholder={placeholder}
            />
        );
    }

    if (!filteredPassTypes?.length && filteredPassTypesRaw?.length) {
        return (
            <div className="pass-types-table-container">
                <table className="table-no-border pass-types-table">
                    <caption>
                        <span className="rec-sr-only">Site passes table</span>
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Pass Type</th>
                            <th scope="col" />
                        </tr>
                    </thead>
                    <tbody>{filteredPassTypesRaw?.map(renderReducedRow)}</tbody>
                </table>
                {pricing}
            </div>
        );
    }

    return (
        <div className="pass-types-table-container">
            <table className="table-no-border pass-types-table">
                <caption>
                    <span className="rec-sr-only">Site pass types</span>
                </caption>
                <thead>
                    <tr>
                        <th scope="col">Pass Type</th>
                        <th scope="col">Duration</th>
                        <th scope="col">Price</th>
                        {showNonResidentFees && (
                            <th scope="col" className="non-resident-column">
                                Non-U.S. Resident Fee
                            </th>
                        )}
                        <th scope="col">Add</th>
                    </tr>
                </thead>
                <tbody>
                    {isEmpty(filteredPassTypes)
                        ? placeholder(showNonResidentFees ? 5 : 4)
                        : filteredPassTypes?.map(renderRow)}
                </tbody>
            </table>
            {pricing}
        </div>
    );
}

function MobilePassTypesTable({
    passTypes,
    passTypesRaw,
    isLoading,
    showDescription = true,
    pricing = null,
    getRowData,
    showNonResidentFees,
    placeholder,
}: MobilePassTypesTableProps) {
    const renderRow = (sitePass: PassType) => {
        const {
            id,
            name,
            usedFor,
            durationText,
            price,
            selectedPass,
            nonResidentFee,
            handleAddPass,
            handleRemoveAllPasses,
        } = getRowData(sitePass);
        const isAnnual = durationText === '12 months';

        return (
            <tr key={`pass-type-${id}`} className={selectedPass ? 'highlight' : ''}>
                <td>
                    {name && (
                        <Text
                            className="pass-types-table__content-title"
                            fontWeight="semibold"
                        >
                            {!showDescription && usedFor ? (
                                <HelpTip
                                    appearance="dashed"
                                    className="mb-half"
                                    triggerText={name}
                                    content={usedFor}
                                />
                            ) : (
                                name
                            )}
                        </Text>
                    )}
                    {usedFor && showDescription && (
                        <Text className="pass-types-table__content-item" size="sm">
                            {usedFor}
                        </Text>
                    )}
                    {showNonResidentFees && isAnnual && (
                        <Text className="pass-types-table__content-item" size="sm">
                            Available for U.S. Residents Only
                        </Text>
                    )}
                    <div className="mb-2">
                        <span className="mr-2">
                            <Text className="mr-half" fontWeight="semibold" tag="span">
                                Duration:
                            </Text>
                            <Text tag="span">{durationText}</Text>
                        </span>
                        <span>
                            <Text className="mr-half" fontWeight="semibold" tag="span">
                                Price:
                            </Text>
                            <Text tag="span">{price}</Text>
                        </span>
                    </div>
                    {showNonResidentFees && (
                        <div className="mb-2">
                            <span className="mr-2">
                                <Text
                                    className="mr-half"
                                    fontWeight="semibold"
                                    tag="span"
                                >
                                    Non-U.S. Resident Fee:
                                </Text>
                                <Text>
                                    {nonResidentFee
                                        ? `$${nonResidentFee} for each non-resident (16 and over)`
                                        : 'Not Applicable'}
                                </Text>
                            </span>
                        </div>
                    )}
                    {!selectedPass ? (
                        <Button
                            className="pass-types-table__content-item pass-types-table__button"
                            appearance="tertiary"
                            shouldFitContainer
                            isDisabled={isLoading}
                            onClick={handleAddPass}
                            gaTrackingId="986199505124"
                            aria-label={`Add Pass - ${name}`}
                        >
                            Add Pass
                        </Button>
                    ) : (
                        <Button
                            className="pass-types-table__content-item pass-types-table__button pass-types-table__button-selected-pass"
                            appearance="tertiary"
                            shouldFitContainer
                            iconBeforeElement={<Icon iconName="check-circle" size="md" />}
                            isDisabled={isLoading}
                            onClick={handleRemoveAllPasses}
                            gaTrackingId="279553950679"
                        >
                            Pass Selected
                        </Button>
                    )}
                </td>
            </tr>
        );
    };

    const renderReducedRow = ({ id, name }: Pick<PassType, 'id' | 'name'>) => (
        <tr key={`pass-type-${id}`}>
            <td>
                <Text fontWeight="semibold">{name}</Text>
                <Text size="sm">Must be purchased on site</Text>
            </td>
        </tr>
    );

    if (!passTypes?.length && passTypesRaw?.length) {
        return (
            <div className="pass-types-table-container">
                <table className="table-no-border pass-types-table">
                    <caption>
                        <span className="rec-sr-only">Site passes table</span>
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Pass Type</th>
                        </tr>
                    </thead>
                    <tbody>{passTypesRaw?.map(renderReducedRow)}</tbody>
                </table>
                {pricing}
            </div>
        );
    }

    return (
        <div className="pass-types-table-container">
            <table className="table-no-border pass-types-table">
                <caption>
                    <span className="rec-sr-only">Site passes table</span>
                </caption>
                <thead>
                    <tr>
                        <th scope="col">Pass Type</th>
                    </tr>
                </thead>
                <tbody>
                    {isEmpty(passTypes) ? placeholder(1) : passTypes?.map(renderRow)}
                </tbody>
            </table>
            {pricing}
        </div>
    );
}
